// Performance V2 — Activity tab (#4297). Unified feed merging Google Ads
// change_events (user edits) with Glitch ActionLog entries (AI optimizations).
// Extracted from performance-v2.tsx (#4932 Phase 0.3); no behavior change.
// The activity-feed query stays in the shell (the chart's edit-dot overlay and
// the Overview widgets share it) — this module owns only presentation and the
// source-filter state.
import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import { BarChart3 } from 'lucide-react';

import type { ActivityEvent } from '@/lib/apis';
import { Card } from '@/components/ui/card';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { cn } from '@/lib/utils';
import { EmptyTabState } from '../cells';

// #4297 — Activity event category classifier + badge.
//
// Maps the raw resource_type (Google Ads change_event) or action_type
// (Glitch ActionLog) plus changed_fields into a small taxonomy the team
// uses in its weekly client reports: Bid Strategy · New Campaign · Ad Copy
// · Negative Keywords · Budget · Ad Group · Keyword (catch-all keyword
// edits that aren't negatives).
type ActivityCategory = 'Bid Strategy' | 'New Campaign' | 'Ad Copy' | 'Negative Keywords' | 'Keyword' | 'Budget' | 'Ad Group' | 'Campaign' | 'Other';

function classifyActivity(event: ActivityEvent): ActivityCategory {
  const rt = (event.resource_type || '').toUpperCase();
  const op = (event.operation || '').toUpperCase();
  const fields = (event.changed_fields || []).map(f => f.toLowerCase());

  // Glitch ActionLog action types — exhaustive map keyed by ActionType enum.
  if (event.source === 'glitch') {
    if (rt.includes('NEGATIVE_KEYWORD')) return 'Negative Keywords';
    if (rt === 'ADD_KEYWORD' || rt === 'DELETE_KEYWORD' || rt === 'PAUSE_KEYWORD' || rt === 'ADD_EXACT_MATCH_KEYWORD') return 'Keyword';
    if (rt === 'SET_BUDGET') return 'Budget';
    if (rt === 'SET_TCPA') return 'Bid Strategy';
    if (rt === 'CREATE_CAMPAIGN' || rt === 'CREATE_INTENT_CAMPAIGN' || rt === 'CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION') return 'New Campaign';
    if (rt === 'SYNC_CAMPAIGN_DATA' || rt === 'GOOGLE_ADS_SYNC') return 'Campaign';
    return 'Other';
  }

  // Google Ads change_event resource types.
  if (rt === 'CAMPAIGN_BUDGET') return 'Budget';
  if (rt === 'BIDDING_STRATEGY' || fields.some(f => f.includes('bidding_strategy') || f.includes('target_cpa') || f.includes('target_roas'))) return 'Bid Strategy';
  if (rt === 'AD_GROUP_AD') return 'Ad Copy';
  if (rt === 'AD_GROUP') return 'Ad Group';
  if (rt === 'AD_GROUP_CRITERION' || rt === 'CAMPAIGN_CRITERION') {
    // Distinguish negatives from regular keyword edits via the changed
    // 'negative' field or a CREATE on a campaign-level criterion.
    if (fields.includes('negative')) return 'Negative Keywords';
    if (rt === 'CAMPAIGN_CRITERION' && op === 'CREATE') return 'Negative Keywords';
    return 'Keyword';
  }
  if (rt === 'CAMPAIGN') {
    if (op === 'CREATE') return 'New Campaign';
    return 'Campaign';
  }
  return 'Other';
}

const CATEGORY_STYLE: Record<ActivityCategory, string> = {
  'Bid Strategy':      'bg-purple-100 text-purple-800',
  'New Campaign':      'bg-blue-100 text-blue-800',
  'Ad Copy':           'bg-pink-100 text-pink-800',
  'Negative Keywords': 'bg-amber-100 text-amber-800',
  'Keyword':           'bg-indigo-100 text-indigo-800',
  'Budget':            'bg-green-100 text-green-800',
  'Ad Group':          'bg-teal-100 text-teal-800',
  'Campaign':          'bg-gray-100 text-gray-700',
  'Other':             'bg-gray-100 text-gray-600',
};

function CategoryBadge({ category }: { category: ActivityCategory }) {
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase whitespace-nowrap', CATEGORY_STYLE[category])}>
      {category}
    </span>
  );
}

// Source dot — coloured by 'user' vs 'glitch'. Pure visual marker, no text.
function SourceDot({ source }: { source: 'user' | 'glitch' }) {
  // Aligned with the Performance Over Time chart's overlay dots so the same
  // event source uses the same visual identity everywhere it appears on
  // perf-v2 (chart pills, chart dots, activity timeline dots, filter chips):
  //   glitch (AI optimizations) → amber
  //   user   (Google Ads edits) → slate
  const cls = source === 'glitch' ? 'bg-amber-500' : 'bg-slate-500';
  return <span className={cn('inline-block w-2 h-2 rounded-full', cls)} aria-hidden="true" />;
}

// Pretty timestamp — "2 hr ago" for recent, absolute date otherwise.
// Exported: the Overview tab's AI-actions widget renders the same format.
export function formatActivityTimestamp(ts: string | null): string {
  if (!ts) return '—';
  // Strip a possible "T" and trailing "Z" so the string parses on Safari too.
  const parsed = new Date(ts.replace(' ', 'T').replace(/Z$/, ''));
  if (Number.isNaN(parsed.getTime())) return ts;
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Compose a human-readable line from the structured event fields.
function describeActivity(event: ActivityEvent): string {
  if (event.source === 'glitch' && event.summary) return event.summary;
  const op = (event.operation || '').toLowerCase();
  const fields = event.changed_fields || [];
  const opLabel = op === 'create' ? 'created' : op === 'update' ? 'updated' : op === 'remove' ? 'removed' : 'changed';
  const fieldLabel = fields.length === 0 ? '' : fields.length === 1
    ? ` (${fields[0]})`
    : ` (${fields.slice(0, 2).join(', ')}${fields.length > 2 ? `, +${fields.length - 2}` : ''})`;
  return `${opLabel}${fieldLabel}`;
}

export function ActivityTab({
  events,
  isLoading,
  layout,
  onReorder,
  onResize,
}: {
  events: ActivityEvent[] | undefined;
  isLoading: boolean;
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const [activitySourceFilter, setActivitySourceFilter] = useState<'all' | 'glitch' | 'user'>('all');

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          case 'activity-content': {
            const hasActivity = Array.isArray(events) && events.length > 0;
            if (isLoading && !hasActivity) {
              return (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" className="text-gray-400" />
                  <span className="ml-3 text-sm text-gray-500">Loading account activity…</span>
                </div>
              );
            }
            if (!events || events.length === 0) {
              return (
                <EmptyTabState
                  icon={BarChart3}
                  title="No Activity"
                  description="No user edits or Glitch actions in this period. Google Ads only retains change history for the past 30 days — try widening the range."
                />
              );
            }
            // Apply the source filter. Counts of each source are useful as
            // affordances on the filter chips so users see how many items
            // are hidden when they narrow the scope.
            const glitchCount = events.filter(e => e.source === 'glitch').length;
            const userCount = events.filter(e => e.source === 'user').length;
            const filteredEvents = activitySourceFilter === 'all'
              ? events
              : events.filter(e => e.source === activitySourceFilter);
            const filterChips: Array<{ value: 'all' | 'glitch' | 'user'; label: string; count: number }> = [
              { value: 'all',    label: 'All',           count: events.length },
              { value: 'glitch', label: 'Optimizations', count: glitchCount },
              { value: 'user',   label: 'User edits',    count: userCount },
            ];
            return (
              <Card className="p-0 border border-gray-200 shadow-sm">
                {/* pl-4 pr-10: keep left padding standard, but reserve
                    40px of right space so the filter chips (right-aligned
                    via justify-between) don't slip under the WidgetGrid
                    resize/reorder kebab at top-2/right-2 of the wrapper. */}
                <div className="pl-4 pr-10 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-900">Activity</h4>
                  {/* Source filter chips. Per-source active colours match
                      the chart's overlay pills so the same source has the
                      same visual identity across surfaces: 'All' stays
                      cobalt (brand-primary default), 'Optimizations' tinted
                      amber, 'User edits' tinted slate. */}
                  <div className="flex items-center gap-1.5">
                    {filterChips.map(chip => {
                      const isActive = activitySourceFilter === chip.value;
                      const isDisabled = chip.count === 0 && chip.value !== 'all';
                      // Active palette per source: matches the chart pills
                      // and the SourceDot colours. 'All' uses brand cobalt
                      // since it has no semantic source identity.
                      const activeClass =
                        chip.value === 'glitch' ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : chip.value === 'user' ? 'bg-slate-100 border-slate-300 text-slate-700'
                        : 'bg-[#290BD9] border-[#290BD9] text-white';
                      // Count-pill colour inside the active state, dialled
                      // back so it stays a secondary affordance.
                      const activeCountClass =
                        chip.value === 'glitch' ? 'text-amber-700'
                        : chip.value === 'user' ? 'text-slate-500'
                        : 'text-white/80';
                      return (
                        <button
                          key={chip.value}
                          type="button"
                          onClick={() => !isDisabled && setActivitySourceFilter(chip.value)}
                          disabled={isDisabled}
                          className={cn(
                            'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors',
                            isActive ? activeClass : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50',
                            isDisabled && 'opacity-40 cursor-not-allowed hover:bg-white',
                          )}
                        >
                          {chip.value !== 'all' && <SourceDot source={chip.value} />}
                          <span>{chip.label}</span>
                          <span className={cn('text-[10px]', isActive ? activeCountClass : 'text-gray-400')}>{chip.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredEvents.length === 0 ? (
                    <div className="px-4 py-12 text-center text-sm text-gray-500">
                      No {activitySourceFilter === 'glitch' ? 'Glitch optimizations' : 'user edits'} in this period.
                    </div>
                  ) : filteredEvents.map((event, i) => {
                    const category = classifyActivity(event);
                    return (
                      <div key={`${event.timestamp ?? 'no-ts'}-${i}`} className="flex items-start gap-3 px-4 py-3">
                        <div className="flex-shrink-0 mt-1.5"><SourceDot source={event.source} /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <span>{formatActivityTimestamp(event.timestamp)}</span>
                            {event.actor && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="truncate max-w-[200px]">{event.actor}</span>
                              </>
                            )}
                            {event.is_undone && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="text-amber-700">undone</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <CategoryBadge category={category} />
                            <span className="text-sm text-gray-900">{describeActivity(event)}</span>
                          </div>
                          {(event.campaign_name || event.ad_group_name) && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                              {event.campaign_name}
                              {event.ad_group_name && <span className="text-gray-400"> · {event.ad_group_name}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 px-4 py-3 border-t border-gray-100">
                  {activitySourceFilter === 'all'
                    ? `${events.length.toLocaleString()} event${events.length === 1 ? '' : 's'} in this period`
                    : `${filteredEvents.length.toLocaleString()} of ${events.length.toLocaleString()} event${events.length === 1 ? '' : 's'} shown`}
                  . User edits come from the Google Ads change history (30-day retention); Glitch optimizations come from the optimisations engine.
                </p>
              </Card>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
